import { useCallback, useEffect, useState } from 'react';
import {
  getSignatures, creerSignature, modifierSignature, supprimerSignature, apercuSignature, attribuerSignature,
  apercuSignatureCompte,
} from '../../lib/api/messagerieApi';
import './Messagerie.css';

/**
 * LES SIGNATURES DE COURRIEL : des modèles de signature qui terminent les
 * messages envoyés depuis la messagerie, attribués aux comptes administrateurs ;
 * le modèle prend les informations et le rôle de la personne qui écrit, dans le
 * thème du site.
 *
 * DES CHOIX, PAS DU CODE : formule, ligne libre, ce qu'on affiche, une couleur
 * du site. La mise en page est celle du site ; le nom, le rôle et l'adresse sont
 * ceux de l'expéditeur au moment de l'envoi. L'aperçu se fait à VOTRE nom.
 *
 * Réservé au super-administrateur, comme le serveur le vérifie.
 */
const COULEURS = [
  { valeur: '#4a6b40', nom: 'Vert Salat Janaza' },
  { valeur: '#6b8f5e', nom: 'Sauge' },
  { valeur: '#2f5d3a', nom: 'Forêt' },
  { valeur: '#3a7c2a', nom: 'Prairie' },
  { valeur: '#7a7060', nom: 'Taupe' },
  { valeur: '#2c2c2c', nom: 'Anthracite' },
];

const VIDE = {
  id: null,
  nom: '',
  formule: '',
  ligneLibre: '',
  telephone: '',
  afficherRole: true,
  afficherAdresse: true,
  afficherSite: true,
  couleur: '#4a6b40',
  parDefaut: false,
};

/** Ce que le serveur attend d'un modèle (sans son id ni ses compteurs). */
const saisieDe = (m) => ({
  nom: m.nom ?? '',
  formule: m.formule ?? '',
  ligneLibre: m.ligneLibre ?? '',
  telephone: m.telephone ?? '',
  afficherRole: Boolean(m.afficherRole),
  afficherAdresse: Boolean(m.afficherAdresse),
  afficherSite: Boolean(m.afficherSite),
  couleur: m.couleur || VIDE.couleur,
  parDefaut: Boolean(m.parDefaut),
});

/**
 * L'aperçu, isolé dans un cadre : le HTML vient du serveur, déjà échappé. Sur
 * téléphone, logo plus petit et marges resserrées, pour que la carte tienne
 * dans la largeur.
 */
const STYLE_APERCU = '<meta name="viewport" content="width=device-width,initial-scale=1">'
  + '<style>@media only screen and (max-width:520px){'
  + '.sig-logo{padding-right:10px!important}.sig-logo img{width:64px!important}'
  + '.sig-texte{padding-left:10px!important;word-break:normal!important}'
  + '.sig-texte div:first-child{font-size:18px!important}'
  + '}</style>';

function Apercu({ html, grand = false }) {
  const page = `<!doctype html><html><head><meta charset="utf-8">${STYLE_APERCU}</head><body style="margin:0;padding:14px 16px;background:#fff;font-family:Inter,-apple-system,'Segoe UI',Helvetica,Arial,sans-serif;">${html ?? ''}</body></html>`;
  return (
    <iframe
      className={`signatures__apercu${grand ? ' signatures__apercu--grand' : ''}`}
      title="Aperçu de la signature"
      sandbox=""
      srcDoc={page}
    />
  );
}

const nomComplet = (s) => [s.prenom, s.nom].filter(Boolean).join(' ') || s.mail;

export default function SignaturesMail() {
  const [donnees, setDonnees] = useState(null);
  const [apercus, setApercus] = useState({});
  const [edition, setEdition] = useState(null);
  const [apercuEdition, setApercuEdition] = useState('');
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  // L'aperçu de la signature d'un compte, en fenêtre.
  const [apercuCompte, setApercuCompte] = useState(null);

  const voirSignature = async (s) => {
    setApercuCompte({ qui: s, html: null });
    try {
      const { data } = await apercuSignatureCompte(s.utilisateurId);
      setApercuCompte({ qui: s, html: data });
    } catch {
      setApercuCompte(null);
      setErreur('L’aperçu de cette signature n’a pas pu être chargé.');
    }
  };

  useEffect(() => {
    if (!apercuCompte) return undefined;
    const surTouche = (e) => { if (e.key === 'Escape') setApercuCompte(null); };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [apercuCompte]);

  const charger = useCallback(async () => {
    try {
      const { data } = await getSignatures();
      setDonnees({ modeles: data?.modeles ?? [], signataires: data?.signataires ?? [] });
      // Un aperçu par modèle, à votre nom.
      const paires = await Promise.all((data?.modeles ?? []).map(async (m) => {
        try {
          const r = await apercuSignature(saisieDe(m));
          return [m.id, r.data];
        } catch {
          return [m.id, ''];
        }
      }));
      setApercus(Object.fromEntries(paires));
    } catch {
      setErreur('Les signatures n’ont pas pu être chargées.');
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // L'aperçu de la saisie suit la frappe, un instant après.
  useEffect(() => {
    if (!edition) return undefined;
    const minuteur = setTimeout(async () => {
      try {
        const { data } = await apercuSignature(saisieDe(edition));
        setApercuEdition(data);
      } catch {
        setApercuEdition('');
      }
    }, 300);
    return () => clearTimeout(minuteur);
  }, [edition]);

  const enregistrer = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      if (edition.id) await modifierSignature(edition.id, saisieDe(edition));
      else await creerSignature(saisieDe(edition));
      setEdition(null);
      await charger();
    } catch (ex) {
      setErreur(ex?.response?.data?.message ?? 'Le modèle n’a pas pu être enregistré.');
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Supprimer le modèle « ${m.nom} » ? Ses comptes reprendront le modèle par défaut.`)) return;
    setErreur(null);
    try {
      await supprimerSignature(m.id);
      await charger();
    } catch (ex) {
      setErreur(ex?.response?.data?.message ?? 'Le modèle n’a pas pu être supprimé.');
    }
  };

  const attribuer = async (utilisateurId, valeur) => {
    setErreur(null);
    try {
      await attribuerSignature(utilisateurId, valeur ? Number(valeur) : null);
      await charger();
    } catch {
      setErreur('Le modèle n’a pas pu être attribué.');
    }
  };

  if (!donnees) {
    return erreur
      ? <div className="alert alert-error" role="alert">{erreur}</div>
      : <p className="roles__attente">Lecture des signatures…</p>;
  }

  const parDefaut = donnees.modeles.find((m) => m.parDefaut);
  const set = (cle) => (e) => setEdition({ ...edition, [cle]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  return (
    <section className="signatures">
      <header className="roles__entete">
        <div>
          <h2>Les signatures</h2>
          <p>
            Elles terminent les messages envoyés depuis la messagerie du support, au nom de celui
            qui écrit : son prénom, son rôle, son adresse. Les aperçus sont à votre nom.
          </p>
        </div>
        {!edition && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setEdition(VIDE)}>
            + Nouveau modèle
          </button>
        )}
      </header>

      {erreur && <div className="alert alert-error" role="alert">{erreur}</div>}

      {edition && (
        <form className="roles__edition signatures__edition" onSubmit={enregistrer}>
          <h3>{edition.id ? `Modifier « ${edition.nom || 'le modèle'} »` : 'Nouveau modèle'}</h3>
          <div className="signatures__grille">
            <div>
              <div className="champ">
                <label htmlFor="sig-nom">Nom du modèle</label>
                <input id="sig-nom" value={edition.nom} onChange={set('nom')} maxLength={60} required
                  placeholder="Signature de l’équipe" />
              </div>
              <div className="champ">
                <label htmlFor="sig-formule">Formule de politesse</label>
                <input id="sig-formule" value={edition.formule ?? ''} onChange={set('formule')} maxLength={80}
                  placeholder="Fraternellement, (facultatif)" />
              </div>
              <div className="champ">
                <label htmlFor="sig-ligne">Ligne libre</label>
                <input id="sig-ligne" value={edition.ligneLibre ?? ''} onChange={set('ligneLibre')} maxLength={160}
                  placeholder="Ne manquez plus aucune prière mortuaire…" />
              </div>
              {/* LE TÉLÉPHONE : sa ligne, sous l'adresse mail. */}
              <div className="champ">
                <label htmlFor="sig-tel">Téléphone</label>
                <input id="sig-tel" type="tel" value={edition.telephone ?? ''} onChange={set('telephone')} maxLength={30}
                  placeholder="06 12 34 56 78 (facultatif)" />
              </div>

              <fieldset className="signatures__options">
                <legend>Afficher</legend>
                <label className="case"><input type="checkbox" checked={edition.afficherRole} onChange={set('afficherRole')} /> Le rôle</label>
                <label className="case"><input type="checkbox" checked={edition.afficherAdresse} onChange={set('afficherAdresse')} /> L’adresse mail</label>
                <label className="case"><input type="checkbox" checked={edition.afficherSite} onChange={set('afficherSite')} /> Le lien salatjanaza.org</label>
              </fieldset>

              <fieldset className="signatures__options">
                <legend>Couleur du nom</legend>
                <div className="signatures__couleurs">
                  {COULEURS.map((c) => (
                    <label
                      key={c.valeur}
                      className={`signatures__couleur${edition.couleur === c.valeur ? ' est-choisie' : ''}`}
                      style={{ '--pastille': c.valeur }}
                      title={c.nom}
                    >
                      <input type="radio" name="sig-couleur" value={c.valeur} checked={edition.couleur === c.valeur}
                        onChange={set('couleur')} aria-label={c.nom} />
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="case">
                <input type="checkbox" checked={edition.parDefaut} onChange={set('parDefaut')} />
                Modèle par défaut (pour qui n’en a pas reçu d’autre)
              </label>
            </div>

            <div className="signatures__apercu-zone">
              <span className="signatures__apercu-titre">Aperçu à votre nom</span>
              <Apercu html={apercuEdition} />
            </div>
          </div>

          <div className="modale__actions">
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setEdition(null)}>Annuler</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={envoi}>{envoi ? 'Enregistrement…' : 'Enregistrer'}</button>
          </div>
        </form>
      )}

      <ul className="signatures__liste">
        {donnees.modeles.map((m) => (
          <li key={m.id} className="roles__carte">
            <div className="roles__tete">
              <strong className="roles__nom">{m.nom}</strong>
              {m.parDefaut && <span className="roles__badge roles__badge--acces">Par défaut</span>}
              {typeof m.nombreComptes === 'number' && (
                <span className="roles__compte">{m.nombreComptes} compte{m.nombreComptes > 1 ? 's' : ''}</span>
              )}
            </div>
            <Apercu html={apercus[m.id]} />
            <div className="roles__actions">
              <button type="button" className="btn-ghost btn-ghost--mini" onClick={() => setEdition({ ...VIDE, ...m })}>Modifier</button>
              {donnees.modeles.length > 1 && (
                <button type="button" className="btn-ghost btn-ghost--mini btn-ghost--danger" onClick={() => supprimer(m)}>Supprimer</button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <section className="signatures__attribution">
        <h3>Qui signe avec quoi</h3>
        <p className="champ__aide">Les comptes qui ouvrent l’administration. « Par défaut » suit le modèle par défaut s’il change.</p>
        {/* SUR TÉLÉPHONE : chaque ligne devient une carte, les en-têtes de colonnes
            passent en libellés (`data-libelle`), et le bouton Aperçu reste visible. */}
        <table className="signatures__table">
          <thead>
            <tr><th scope="col">Compte</th><th scope="col">Rôle</th><th scope="col">Signature</th><th scope="col">Aperçu</th></tr>
          </thead>
          <tbody>
            {donnees.signataires.length === 0 && (
              <tr><td colSpan={4} className="signatures__non">Aucun compte administrateur.</td></tr>
            )}
            {donnees.signataires.map((s) => (
              <tr key={s.utilisateurId}>
                <td data-libelle="Compte">
                  <strong>{nomComplet(s)}</strong>
                  <span className="signatures__mail">{s.mail}</span>
                </td>
                <td data-libelle="Rôle">{s.role ?? '—'}</td>
                <td data-libelle="Signature">
                  <select value={s.signatureId ?? ''} onChange={(e) => attribuer(s.utilisateurId, e.target.value)}
                    aria-label={`Signature de ${s.mail}`} className="role-parent">
                    <option value="">Par défaut{parDefaut ? ` (${parDefaut.nom})` : ''}</option>
                    {donnees.modeles.map((m) => <option key={m.id} value={m.id}>{m.nom}</option>)}
                  </select>
                </td>
                <td data-libelle="Aperçu" className="signatures__cellule-apercu">
                  <button type="button" className="btn-ghost btn-ghost--mini" onClick={() => voirSignature(s)}>
                    Aperçu
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* LA SIGNATURE D'UN COMPTE, TELLE QUE SES DESTINATAIRES LA VERRONT. */}
      {apercuCompte && (
        <div className="modale signatures-modale" onMouseDown={(e) => { if (e.target === e.currentTarget) setApercuCompte(null); }}>
          <div
            className="modale__boite signatures__fenetre"
            role="dialog"
            aria-modal="true"
            aria-label={`Signature de ${apercuCompte.qui.mail}`}
          >
            <header className="signatures__fenetre-tete">
              <div>
                <h3>Signature de {nomComplet(apercuCompte.qui)}</h3>
                <p>Telle que ses destinataires la verront.</p>
              </div>
              <button
                type="button"
                className="messagerie-grand__fermer"
                aria-label="Fermer"
                onClick={() => setApercuCompte(null)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none"
                  stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </header>
            {apercuCompte.html === null
              ? <p className="roles__attente">Un instant…</p>
              : <Apercu html={apercuCompte.html} grand />}
            <div className="signatures__fenetre-pied">
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setApercuCompte(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
